import { Card, CardBody, CardHeader, CardTitle, ProgressBar, cn } from '@ludus/ui'
import { ArrowRight, ArrowLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { StageMetrics } from '../../types.ts'

interface ConversionMathProps {
  metrics: StageMetrics | null
  prevStageName: string | null
  nextStageName: string | null
}

export function ConversionMath({ metrics, prevStageName, nextStageName }: ConversionMathProps) {
  const { t } = useTranslation()

  // All-leads view has no focused stage, so there's no adjacent-stage conversion
  // to compute. Rather than unmount the card (which makes the whole side panel
  // jump as the stage selection toggles), keep it in place as an inactive
  // placeholder — dimmed, dashed values, with a hint to pick a stage.
  const inactive = !metrics
  const hasAny = !inactive && (metrics.fromPrev !== null || metrics.toNext !== null)

  return (
    <Card className={cn('rounded-xl border-border shadow-none', inactive && 'opacity-60')}>
      <CardHeader className="border-border/20 py-2.5">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
          {t('pages.pipelines.side.conversionTitle', { defaultValue: 'Conversion math' })}
        </CardTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 py-3" aria-disabled={inactive || undefined}>
        {inactive ? (
          <>
            <ConversionRow
              direction="from"
              stageName={null}
              value={null}
              tone="success"
              placeholderLabel={t('pages.pipelines.side.from', { defaultValue: 'From' })}
            />
            <ConversionRow
              direction="to"
              stageName={null}
              value={null}
              tone="primary"
              placeholderLabel={t('pages.pipelines.side.to', { defaultValue: 'To' })}
            />
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
              {t('pages.pipelines.side.conversionInactive', {
                defaultValue: 'Select a stage to compare',
              })}
            </span>
          </>
        ) : (
          <>
            {!hasAny && (
              <span className="text-xs text-muted">
                {t('pages.pipelines.side.terminalStage', {
                  defaultValue: 'Terminal stage — no adjacent conversion math.',
                })}
              </span>
            )}
            {metrics.fromPrev !== null && (
              <ConversionRow
                direction="from"
                stageName={prevStageName}
                value={metrics.fromPrev}
                tone="success"
                placeholderLabel={t('pages.pipelines.side.from', { defaultValue: 'From' })}
              />
            )}
            {metrics.toNext !== null && (
              <ConversionRow
                direction="to"
                stageName={nextStageName}
                value={metrics.toNext}
                tone="primary"
                placeholderLabel={t('pages.pipelines.side.to', { defaultValue: 'To' })}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

function ConversionRow({
  direction,
  stageName,
  value,
  tone,
  placeholderLabel,
}: {
  direction: 'from' | 'to'
  stageName: string | null
  value: number | null
  tone: 'primary' | 'success'
  placeholderLabel: string
}) {
  const Icon = direction === 'from' ? ArrowLeft : ArrowRight
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mono text-2xl font-bold tabular-nums tracking-tight text-foreground">
          {value === null ? '—' : `${value}%`}
        </span>
        <span
          className={cn(
            'flex min-w-0 items-center gap-1 text-right text-[10px] font-semibold uppercase tracking-[0.14em] text-muted',
          )}
        >
          <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
          <span className="truncate">{stageName ?? placeholderLabel}</span>
        </span>
      </div>
      <ProgressBar value={value ?? 0} tone={tone} size="sm" />
    </div>
  )
}
